import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    InputBaseComponentProps,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { forwardRef } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { useRestParametersForm } from 'hooks/restParameters/useRestParametersForm';
import { nameValidation } from 'utils/Validations';
import { useTranslation } from 'react-i18next';
import { IMaskInput, IMask } from 'react-imask';

interface Props {
    form: ReturnType<typeof useRestParametersForm>;
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
}

const TextMaskHour = forwardRef<HTMLInputElement, InputBaseComponentProps>(function TextMaskHour(props, ref) {
    const { onChange, name, ...other } = props;

    return (
        <IMaskInput
            {...other}
            mask="HH:MM"
            definitions={{
                H: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 2,
                },
                M: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 5,
                },
            }}
            blocks={{
                HH: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 24,
                    maxLength: 2,
                },
                MM: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 59,
                    maxLength: 2,
                },
            }}
            prepare={(value: any, masked: any) => {
                const parts = value.split(':');
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);

                    if (hours === 24 && minutes > 0) {
                        return '24:00';
                    }
                }
                return value;
            }}
            validate={(value: any, { blocks }: any) => {
                const parts = value.split(':');
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);

                    if (hours === 24 && minutes > 0) {
                        return false;
                    }
                }
                return true;
            }}
            placeholder={'24:00'}
            inputRef={ref}
            onAccept={(value: any) => props.onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

export const BreakRuleForm = ({ form, mode }: Props) => {
    const { t } = useTranslation();
    const { existName, form: formState } = form;
    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    fullWidth
                    required
                    name="name"
                    label={t('settings.break_rules.dialog.inputs.name')}
                    value={formState.name}
                    autoComplete="off"
                    onChange={(e) => {
                        if (nameValidation(e.target.value)) {
                            form.update('name', e.target.value);
                            existName(e.target.value);
                        }
                    }}
                    error={mode !== 'info' && existName(formState.name)}
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${formState.name.length}/100`}</InputAdornment>,
                        },
                    }}
                    disabled={mode === 'info'}
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>
                        {mode !== 'info' && existName(formState.name) ? t('settings.fixed_rules.dialog.inputs.exist_name') : ''}
                    </Box>
                </FormHelperText>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.break_duration')}</Typography>
            </Grid>

            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <TextField
                    fullWidth
                    required
                    name="duration_normal"
                    label={t('settings.break_rules.dialog.inputs.duration_normal')}
                    value={formState.duration_normal}
                    autoComplete="off"
                    onChange={(e) => form.update('duration_normal', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateDurationNormal(formState.duration_normal) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Duración media debe ser superior a duración normal" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.duration_normal ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.validateDurationNormal(formState.duration_normal) || form.showError.duration_normal}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <TextField
                    fullWidth
                    required
                    name="duration_medio"
                    label={t('settings.break_rules.dialog.inputs.duration_medium')}
                    value={formState.duration_medio}
                    autoComplete="off"
                    onChange={(e) => form.update('duration_medio', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateDurationMedio(formState.duration_medio) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Duración media debe estar entre duración normal y máxima" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.duration_medio ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.validateDurationMedio(formState.duration_medio) || form.showError.duration_medio}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <TextField
                    fullWidth
                    required
                    name="duration_max"
                    label={t('settings.break_rules.dialog.inputs.duration_max')}
                    value={formState.duration_max}
                    autoComplete="off"
                    onChange={(e) => form.update('duration_max', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateDurationMax(formState.duration_max) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Duración máxima tiene que ser mayor a duración media" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.duration_max ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.validateDurationMax(formState.duration_max) || form.showError.duration_max}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.roundings_grace_periods')}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControlLabel
                    control={<Checkbox name="use_rounding_punch_unplan" checked={formState.use_rounding_punch_unplan} onChange={(e) => form.update('use_rounding_punch_unplan', e.target.checked)} />}
                    label={t('settings.break_rules.dialog.inputs.use_rounding_punch_unplan')}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.shortened_break')}</Typography>
                <Typography variant="caption" display="block">
                    {t('settings.break_rules.dialog.inputs.break_duration<normal_duration')}
                </Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="break_short_round"
                    label={t('settings.break_rules.dialog.inputs.break_short_round')}
                    value={formState.break_short_round}
                    autoComplete="off"
                    onChange={(e) => form.update('break_short_round', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateShortRoundVsGrace(formState.break_short_round) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Redondeo acortado debe ser superior al período de gracia acortado" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.break_short_round ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    disabled={mode === 'info' || parseInt(formState.duration_normal.replace(':', '')) === 0 || formState.use_rounding_punch_unplan}
                    error={form.validateShortRoundVsGrace(formState.break_short_round) || form.showError.break_short_round}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="break_short_grace_period"
                    label={t('settings.break_rules.dialog.inputs.break_short_grace_period')}
                    value={formState.break_short_grace_period}
                    autoComplete="off"
                    onChange={(e) => form.update('break_short_grace_period', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateShortGraceVsRound(formState.break_short_grace_period) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Período de gracia acortado debe ser menor a redondeo acortado." arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.break_short_grace_period ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    disabled={mode === 'info' || parseInt(formState.duration_normal.replace(':', '')) === 0 || formState.use_rounding_punch_unplan}
                    error={form.validateShortGraceVsRound(formState.break_short_grace_period) || form.showError.break_short_grace_period}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.break_intermediate')}</Typography>
                <Typography variant="caption" display="block">
                    {t('settings.break_rules.dialog.inputs.norma_break_average_duration')}
                </Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="break_intermediate_round"
                    label={t('settings.break_rules.dialog.inputs.break_intermediate_round')}
                    value={formState.break_intermediate_round}
                    autoComplete="off"
                    onChange={(e) => form.update('break_intermediate_round', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateIntermediateRoundVsGrace(formState.break_intermediate_round) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Redondeo intermedio debe ser superior al período de gracia intermedio" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.break_intermediate_round ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    disabled={mode === 'info' || parseInt(formState.duration_medio.replace(':', '')) === 0 || formState.use_rounding_punch_unplan}
                    error={form.validateIntermediateRoundVsGrace(formState.break_intermediate_round) || form.showError.break_intermediate_round}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="break_intermediate_grace_period"
                    label={t('settings.break_rules.dialog.inputs.break_intermediate_grace_period')}
                    value={formState.break_intermediate_grace_period}
                    autoComplete="off"
                    onChange={(e) => form.update('break_intermediate_grace_period', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateIntermediateGraceVsRound(formState.break_intermediate_grace_period) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Período de gracia intermedio debe ser menor a redondeo intermedio." arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.break_intermediate_grace_period ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    disabled={mode === 'info' || parseInt(formState.duration_medio.replace(':', '')) === 0 || formState.use_rounding_punch_unplan}
                    error={form.validateIntermediateGraceVsRound(formState.break_intermediate_grace_period) || form.showError.break_intermediate_grace_period}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.break_long')}</Typography>
                <Typography variant="caption" display="block">
                    {t('settings.break_rules.dialog.inputs.media_break_max_duration')}
                </Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="break_long_round"
                    label={t('settings.break_rules.dialog.inputs.break_long_round')}
                    value={formState.break_long_round}
                    autoComplete="off"
                    onChange={(e) => form.update('break_long_round', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateLongRoundVsGrace(formState.break_long_round) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Redondeo prolongado debe ser superior al período de gracia prolongado" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.break_long_round ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    disabled={mode === 'info' || formState.use_rounding_punch_unplan}
                    error={form.validateLongRoundVsGrace(formState.break_long_round) || form.showError.break_long_round}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="break_long_grace_period"
                    label={t('settings.break_rules.dialog.inputs.break_long_grace_period')}
                    value={formState.break_long_grace_period}
                    autoComplete="off"
                    onChange={(e) => form.update('break_long_grace_period', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.validateLongGraceVsRound(formState.break_long_grace_period) ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title="Redondeo prolongado debe ser superior al período de gracia prolongado" arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : form.showError.break_long_grace_period ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    disabled={mode === 'info' || formState.use_rounding_punch_unplan}
                    error={form.validateLongGraceVsRound(formState.break_long_grace_period) || form.showError.break_long_grace_period}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.break_settings')}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1">{t('settings.break_rules.dialog.inputs.start_of_break')}</Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="start_break_after"
                    label={t('settings.break_rules.dialog.inputs.start_break_after')}
                    value={formState.start_break_after}
                    autoComplete="off"
                    onChange={(e) => form.update('start_break_after', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.showError.start_break_after ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.showError.start_break_after}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    fullWidth
                    required
                    name="start_break_before"
                    label={t('settings.break_rules.dialog.inputs.start_break_before')}
                    value={formState.start_break_before}
                    autoComplete="off"
                    onChange={(e) => form.update('start_break_before', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.showError.start_break_before ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.showError.start_break_before}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl required fullWidth disabled={mode === 'info'}>
                    <InputLabel id="start_break_by-select-label">{t('settings.break_rules.dialog.inputs.start_break_by')}</InputLabel>
                    <Select
                        fullWidth
                        labelId="start_break_by-select-label"
                        id="start_break_by-select"
                        name="start_break_by"
                        label={t('settings.break_rules.dialog.inputs.start_break_by')}
                        value={formState.start_break_by}
                        autoComplete="off"
                        onChange={(e) => form.update('start_break_by', e.target.value)}
                    >
                        <MenuItem key="midnight" value="midnight">
                            {t('settings.break_rules.dialog.inputs.start_break_by_data.midnight')}
                        </MenuItem>
                        <MenuItem key="planned_start_time" value="planned_start_time">
                            {t('settings.break_rules.dialog.inputs.start_break_by_data.planned_start_time')}
                        </MenuItem>
                        <MenuItem key="real_start_time" value="real_start_time">
                            {t('settings.break_rules.dialog.inputs.start_break_by_data.real_start_time')}
                        </MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <TextField
                    fullWidth
                    required
                    name="start_break_amount_pay"
                    label={t('settings.break_rules.dialog.inputs.start_break_amount_pay')}
                    value={formState.start_break_amount_pay}
                    autoComplete="off"
                    onChange={(e) => form.update('start_break_amount_pay', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.showError.start_break_amount_pay ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.showError.start_break_amount_pay}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <TextField
                    fullWidth
                    required
                    name="start_break_limit_except_short"
                    label={t('settings.break_rules.dialog.inputs.start_break_limit_except_short')}
                    value={formState.start_break_limit_except_short}
                    autoComplete="off"
                    onChange={(e) => form.update('start_break_limit_except_short', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.showError.start_break_limit_except_short ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.showError.start_break_limit_except_short}
                    disabled={mode === 'info'}
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <TextField
                    fullWidth
                    required
                    name="start_break_limit_except_long"
                    label={t('settings.break_rules.dialog.inputs.start_break_limit_except_long')}
                    value={formState.start_break_limit_except_long}
                    autoComplete="off"
                    onChange={(e) => form.update('start_break_limit_except_long', e.target.value)}
                    slotProps={{
                        input: {
                            inputComponent: TextMaskHour,
                            endAdornment: form.showError.start_break_limit_except_long ? (
                                <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                    <Tooltip title={'El formato es HH:mm'} arrow>
                                        <InfoIcon color="error" />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    error={form.showError.start_break_limit_except_long}
                    disabled={mode === 'info'}
                />
            </Grid>
        </Grid>
    );
};
